import { reactive } from "vue";
import { trackBook } from "../slideshow/track";
import {
  fetchMemoryBook,
  type MemoryBook,
  type MemoryBookOffer,
  type UpcomingBook,
} from "./memory-book";

export type PanelSource = "button" | "cover" | "return" | "section";

/**
 * What the highlights block and the memory book's panel share: they are separate apps,
 * since the panel must outlive the block (which disappears without cards) and sit outside
 * its inert slides.
 */
export const bookPanel = reactive({
  book: null as MemoryBook | null,
  /** Bumped by "Bläddra i boken": the section scrolls into view, opens and turns by itself. */
  browse: 0,
  open: false,
  /** Whether the page shows the book's own section (Aktuellt then browses there). */
  section: false,
  /** The section's id (`minnesbok`), for the shared link; '' without the section. */
  sectionId: "",
  /** Stripe's session when the payer has come back to the page. */
  session: null as string | null,
  /** While nothing is for sale: the schedule will sell the book here (the highlights' card). */
  upcoming: null as UpcomingBook | null,
});

/** Aktuellt's "Bläddra i boken" and its cover: to the book's section, open, turning by itself. */
export function browseBook() {
  bookPanel.browse += 1;
}

let postId = "";
let loading: Promise<void> | null = null;
/** Counts asks for the book: the newest ask has the say. */
let generation = 0;
/** The ask whose answer is on the page. */
let applied = 0;
/** An older ask's answer that came in after a newer ask began: used if the newer one fails. */
let kept: { asked: number; offer: MemoryBookOffer } | null = null;
/** The newest ask that failed outright (a renewal while offline). */
let failedAsk = 0;

function apply(asked: number, offer: MemoryBookOffer) {
  if (asked < applied) {
    return;
  }
  applied = asked;
  kept = null;
  bookPanel.book = offer.book;
  bookPanel.upcoming = offer.upcoming;
}

/**
 * An answer to an older ask than the newest: kept, unless the newest has already failed, when it
 * is the best answer there is (a reload's new share must not be lost to a failed renewal).
 */
function superseded(asked: number, offer: MemoryBookOffer) {
  if (offer.failed) {
    return;
  }
  if (failedAsk === generation) {
    apply(asked, offer);
  } else if (!kept || asked > kept.asked) {
    kept = { asked, offer };
  }
}

/** After a refused image: at most one new ask for the book per this many ms. */
const RENEW_MS = 60_000;
let renewedAt = 0;

/**
 * Asks for the book; Aktuellt and the book's section mount together and share one ask. `fresh`
 * skips our short cache after the family shares again.
 */
export function loadMemoryBook(id: string, fresh = false): Promise<void> {
  if (loading && id === postId && !fresh) {
    return loading;
  }
  postId = id;
  generation += 1;
  const asked = generation;
  const ask = fetchMemoryBook(id, fresh).then((offer) => {
    // A renewal may have started after this one.
    if (asked === generation) {
      apply(asked, offer);
    } else {
      superseded(asked, offer);
    }
  });
  const pending: Promise<void> = ask.finally(() => {
    if (loading === pending) {
      loading = null;
    }
  });
  loading = pending;
  return pending;
}

export function reloadMemoryBook() {
  return postId ? loadMemoryBook(postId, true) : Promise.resolve();
}

/** The preview addresses are signed for an hour: when one is refused, ask for new ones. */
export function renewBookUrls() {
  const now = Date.now();
  if (now - renewedAt < RENEW_MS) {
    return;
  }
  renewedAt = now;
  if (!postId) {
    return;
  }
  generation += 1;
  const asked = generation;
  fetchMemoryBook(postId, true).then((offer) => {
    // A newer ask (a reload after a purchase error) began meanwhile: it has the say.
    if (asked !== generation) {
      superseded(asked, offer);
      return;
    }
    // Offline or the API down: the book on the page stays (or an older ask's answer that came
    // in meanwhile), and it asks again once back online.
    if (offer.failed) {
      failedAsk = asked;
      if (kept) {
        apply(kept.asked, kept.offer);
      }
      renewedAt = 0;
      window.addEventListener("online", renewBookUrls, { once: true });
      return;
    }
    // An answer: fresh addresses, or a book withdrawn or put off since.
    apply(asked, offer);
  });
}

export function openBookPanel(source: PanelSource) {
  bookPanel.open = true;
  trackBook({ action: "open", source });
}

export function closeBookPanel() {
  bookPanel.open = false;
}
